import noop from "lodash/noop"
import isEmpty from "lodash/isEmpty"
import { ReactSVG } from "react-svg"
import getContentUrl from "utils-lib/getContentUrl"
import isSupportPage from "utils-lib/isSupportPage"
import { makeStyles } from "tss-react/mui"
import { wmColor } from "themes-lib/wmTheme"
import { BOOTSTRAP_GRID_LG_MIN } from "constants-lib/dimensions"
import { KEY_CODE_MAPPING } from "constants-lib/keyCodes"
import {
  getAdobeLaunchAttribute,
  getAdobeLaunchClassName,
} from "utils-lib/analytics/launch"
import { styles } from "constants-lib/styles"
import { getWindow } from "utils-lib/getWindow"
import { isTeamsitePage } from "utils-lib/isTeamsitePage"
import "./MenuItem.scss"
import { isMyWMApp } from "utils-lib/isMyWMApp"
import { getSSRContext } from "contexts-lib/SSRContext"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import withRouter from "hoc-lib/router"
import HoverIntent from "hooks-lib/hoverIntent"
import { Link } from "../../../../../Base"

const useStyles = makeStyles()(() => ({
  lightBorderBottom: {
    borderBottom: `1px solid ${wmColor.gray2} !important`,
  },
  menuHealth: {
    "& a.linkHealth": {
      borderBottom: `1px solid transparent !important`,
      marginTop: "1px",
    },
    "& a.linkHealth:hover": {
      textDecoration: "none !important",
      borderBottom: `1px solid ${wmColor.green1} !important`,
    },
  },
}))

const MenuItem = ({
  closeMegaNav,
  firstLevelNavigationText = ``,
  secondLevelLinkUrl = ``,
  firstLevelNavigationLink = "",
  isExternalLink,
  megaNavSelection = "",
  onMegaNavSelection,
  onMegaNavSubviewSelection = null,
  secondLevelSections = [],
  router,
  isNewDashboard = false,
  tabIndex = "0",
}) => {
  const { classes } = useStyles()
  const ssrWindow = getWindow()
  const { isSSR } = getSSRContext()
  const { LDFFMobileSupportMenuNavigationFix } = useFeatureFlags([
    "LDFFMobileSupportMenuNavigationFix",
  ])

  const isSelected = megaNavSelection === firstLevelNavigationText
  const style = {
    borderBottom: `3px solid transparent`,
    color: isSelected ? styles.footer.legalLinksActive.color : `#4F4F58`,
    position: `relative`,
    textDecoration: `none`,
    cursor: `pointer`,
    paddingBottom: `21px`,
  }

  // iPad cannot hover so show mobile nav
  const windowWidth = ssrWindow.innerWidth

  const showMegaNav =
    windowWidth >= BOOTSTRAP_GRID_LG_MIN &&
    ssrWindow.navigator.userAgent.match(/iPad/i) === null &&
    !isNewDashboard

  const clickLabel = firstLevelNavigationText

  const analyticsClass = getAdobeLaunchClassName({ disabled: false })

  const isExternalLinkFinal =
    isMyWMApp || isTeamsitePage(ssrWindow.location.pathname) || isExternalLink

  const linkProps = {
    ...getAdobeLaunchAttribute({
      value: clickLabel,
    }),
  }

  if (showMegaNav) {
    linkProps.className = `menu-item__text bg-transparent pt-4 linkHealth ${analyticsClass} ${
      isSelected ? `mega-menu` : ``
    }`
    linkProps.style = style
  } else {
    linkProps.className = `d-block d-flex bg-transparent w-100 align-items-center no-outline-focus-link ${analyticsClass}`
    linkProps.style = {
      padding: `1px 7px`,
    }
  }

  if (isSSR) {
    linkProps.className = `${linkProps.className} router-link`
  }

  if (firstLevelNavigationLink) {
    linkProps.href = `${firstLevelNavigationLink}`

    if (isExternalLinkFinal) {
      linkProps.onClick = () => {
        onMegaNavSelection()
      }
    } else {
      // This code isn't needed in canada-app because canada-app uses external links while the AEM code includes internal links
      linkProps.onClick = (e) => {
        const isTargetSupportPage = isSupportPage(firstLevelNavigationLink)

        e.preventDefault()
        onMegaNavSelection()
        if (isTargetSupportPage) {
          if (LDFFMobileSupportMenuNavigationFix) {
            ssrWindow.location.href = firstLevelNavigationLink
          } else {
            ssrWindow.location.pathname = firstLevelNavigationLink
          }
        } else {
          router.push(firstLevelNavigationLink)
        }
        closeMegaNav()
      }
    }
  }

  const handleInteraction = () =>
    onMegaNavSubviewSelection
      ? onMegaNavSubviewSelection(firstLevelNavigationText)
      : onMegaNavSelection(firstLevelNavigationText)

  const handleFocus = () => {
    const megaNavEl = ssrWindow.document.getElementById(`meganav-menu`)

    if (!isEmpty(megaNavEl)) {
      return closeMegaNav()
    }

    return noop()
  }

  let link = (
    <button
      tabIndex={tabIndex}
      className="wm-link-light-green"
      type="button"
      data-testid={firstLevelNavigationText}
      aria-expanded={isSelected}
      // eslint-disable-next-line consistent-return
      onKeyDown={(e) => {
        const megaNavEl = ssrWindow.document.getElementById(`meganav-menu`)

        /* while focus is on a MemnuItem, tabbing should move focus to the first link in the MegaNav */
        if (e.keyCode === KEY_CODE_MAPPING.TAB) {
          if (e.shiftKey) {
            const menuItems = Array.from(
              ssrWindow.document.querySelectorAll(`.menu-item__text`),
            )
            const isCurrentMenutItem = e.target === menuItems[0]

            if (isCurrentMenutItem) {
              if (megaNavEl) {
                closeMegaNav()
              }
            }
          } else if (megaNavEl) {
            e.preventDefault()

            const firstLink = ssrWindow.document.querySelector(
              `#meganav-menu a:first-child`,
            )
            firstLink && firstLink.focus()
          }
        }

        if (
          e.keyCode === KEY_CODE_MAPPING.ENTER ||
          e.keyCode === KEY_CODE_MAPPING.SPACE_BAR
        ) {
          e.preventDefault()

          const megaNavEl = ssrWindow.document.getElementById(`meganav-menu`)

          if (!isEmpty(megaNavEl)) {
            return closeMegaNav()
          }
          handleInteraction()
        }
      }}
      {...linkProps}
      id={`divItem ${firstLevelNavigationText}`}
      onClick={handleInteraction}
      onFocus={handleFocus}
    >
      {firstLevelNavigationText}
    </button>
  )
  const SUPPORT_STRING = "Support"
  const SUPPORT_OVERRIDE_CLASS =
    "menu-item__text bg-transparent pt-4 a-taggable"

  if (firstLevelNavigationLink) {
    link = isExternalLinkFinal ? (
      <a
        tabIndex={tabIndex}
        id="mywm-dashboard-support-link"
        className="bg-transparent wm-link-light-green"
        data-header-selection={firstLevelNavigationText}
        {...linkProps}
        onKeyDown={(e) => {
          /* handle spacebar event */
          if (e.keyCode === KEY_CODE_MAPPING.SPACE_BAR) {
            router.push(linkProps.href)
          }
          if (e.keyCode === KEY_CODE_MAPPING.TAB) {
            e.preventDefault()
            ssrWindow.document.querySelector(`[data-testid="Link"]`).focus()
          }
        }}
      >
        {firstLevelNavigationText}
      </a>
    ) : (
      <Link
        data-header-selection={firstLevelNavigationText}
        {...linkProps}
        // FIXME: This is a hack to compare the support string and append these specific classes to that menu item. It already has those classes. but the cascade puts them in a different order between mywm & aem spa. This unifies them.
        overrideClass={
          firstLevelNavigationText === SUPPORT_STRING
            ? SUPPORT_OVERRIDE_CLASS
            : null
        }
        {...(!LDFFMobileSupportMenuNavigationFix && {
          onClick: () => {
            // clean hash from url when navigating to support page
            if (firstLevelNavigationText === SUPPORT_STRING) {
              router.push({ pathname: linkProps.href, hash: "" })
            }
          },
        })}
      >
        {firstLevelNavigationText}
      </Link>
    )
  }

  const navContent = showMegaNav ? (
    // Meganav
    <HoverIntent
      onMouseOver={handleInteraction}
      onMouseOut={() => {}}
      sensitivity={20}
      interval={100}
    >
      <div
        // renamed this classname as it conflicted with the other MenuItem scss file class which causes an issue in SSR env
        className={`bottom-menu-item position-relative d-flex ${classes.menuHealth}`}
        style={{
          padding: `0 1.55rem`,
          height: "4.5rem",
        }}
      >
        {link}
      </div>
    </HoverIntent>
  ) : secondLevelLinkUrl ? (
    <div className="bottomNavItems has-focus">
      {isExternalLinkFinal ? (
        <Link
          data-header-selection={firstLevelNavigationText}
          {...linkProps}
          style={{
            color: `#024731 !important`,
            fontSize: 18,
            height: `3.5rem`,
          }}
          className="has-focus"
          onKeyDown={(e) => {
            /* handle spacebar event */
            if (e.keyCode === KEY_CODE_MAPPING.SPACE_BAR) {
              router.push(linkProps.href)
            }
          }}
        >
          {firstLevelNavigationText}
        </Link>
      ) : (
        <a
          data-header-selection={firstLevelNavigationText}
          {...linkProps}
          style={{
            color: `#67696d !important`,
            fontSize: 18,
            height: `3.5rem`,
          }}
          onKeyDown={(e) => {
            /* handle spacebar event */
            if (e.keyCode === KEY_CODE_MAPPING.SPACE_BAR) {
              router.push(linkProps.href)
            }
          }}
        >
          {firstLevelNavigationText}
        </a>
      )}
    </div>
  ) : (
    // Mobile nav
    <div
      className={`bottomNavItems has-focus-within d-flex justify-content-between align-items-center ${
        isNewDashboard && classes.lightBorderBottom
      }`}
      onClick={
        isEmpty(secondLevelSections) ? onMegaNavSelection : handleInteraction
      }
    >
      <div className="menu-item d-flex align-items-center">{link}</div>
      {!isEmpty(secondLevelSections) && (
        <span className="wm-cursor-pointer">
          <ReactSVG
            wrapper="span"
            src={getContentUrl(
              isSelected
                ? `/content/dam/wm/icons/controls/20x20/green/collapse.svg`
                : `/content/dam/wm/icons/controls/20x20/green/expand.svg`,
            )}
          />
        </span>
      )}
    </div>
  )

  return (
    <div className="MenuItem maax-font-weight-bold" data-testid="MenuItem">
      {navContent}
    </div>
  )
}

// // Note: React 19 no-longer supports propTypes. Leaving this for reference.
// MenuItemLegacy.propTypes = {
//   closeMegaNav: PropTypes.func.isRequired,
//   firstLevelNavigationText: PropTypes.string,
//   secondLevelLinkUrl: PropTypes.string,
//   firstLevelNavigationLink: PropTypes.string,
//   isExternalLink: PropTypes.bool.isRequired,
//   isSelected: PropTypes.bool,
//   megaNavSelection: PropTypes.string,
//   onMegaNavSelection: PropTypes.func.isRequired,
//   onMegaNavSubviewSelection: PropTypes.func,
//   secondLevelSections: PropTypes.array,
//   router: PropTypes.object.isRequired,
//   isNewDashboard: PropTypes.bool,
//   tabIndex: PropTypes.string,
// }

// // Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// MenuItemLegacy.defaultProps = {
//   secondLevelLinkUrl: ``,
//   firstLevelNavigationLink: "",
//   firstLevelNavigationText: ``,
//   // this is an unused prop, removed in the migrated components
//   isSelected: false,
//   megaNavSelection: "",
//   secondLevelSections: [],
//   isNewDashboard: false,
//   onMegaNavSubviewSelection: null,
//   tabIndex: "0",
// }

export default withRouter(MenuItem)
